import {AnalyticsReporter} from '@stripe-internal/analytics-js';
import {assertUnreachable} from '../utils/assertUnreachable';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {isDevEnvironment} from '../utils/isDevEnvironment';
import {isAnalyticsDisabled} from './isAnalyticsDisabled';
import type {
  AnalyticsRecord,
  IAnalyticsEvent,
  IAnalyticsSender,
} from './AnalyticsTypes';
import {getPageViewId} from './getPageViewId';
import {submerchantAnalyticsParams} from '../data-layer-client/submerchantAnalyticsParams';

export class AnalyticsWrapper implements IAnalyticsSender {
  public static instance: AnalyticsWrapper = new AnalyticsWrapper();

  private analyticsClient: AnalyticsReporter<string>;

  public observabilityAuthMetadata: AnalyticsRecord | undefined;

  constructor() {
    const {serviceEnvironment} = getCurrentScriptUrlContext();
    this.analyticsClient = new AnalyticsReporter({
      stripeCookiesEnforcementMode: 'restricted',
      inProduction: serviceEnvironment === 'prod' && !isDevEnvironment, // We only log to t.stripe.com in production, but not when tunneling
      clientId: 'connect-js', // This has to match our AEL clientid: https://git.corp.stripe.com/stripe-internal/zoolander/blob/2396aa220534b507677479ed6abf8da6a3e68ee4/uppsala/src/main/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L598
      version: process.env.COMMIT_HASH,
      useCookies: 'disabled',
      safeParams: submerchantAnalyticsParams,
      pageviewtoken: getPageViewId(), // This helps correlate events for a given session together
    });
  }

  track(id: string, params?: AnalyticsRecord) {
    if (isAnalyticsDisabled()) {
      return;
    }

    this.analyticsClient.track(id, this.paramsWithAuthMetadata(params));
  }

  viewed(id: string, params?: AnalyticsRecord) {
    if (isAnalyticsDisabled()) {
      return;
    }

    this.analyticsClient.viewed(id, this.paramsWithAuthMetadata(params));
  }

  action(id: string, params?: AnalyticsRecord) {
    if (isAnalyticsDisabled()) {
      return;
    }

    this.analyticsClient.action(id, this.paramsWithAuthMetadata(params));
  }

  link(id: string, params?: AnalyticsRecord) {
    if (isAnalyticsDisabled()) {
      return;
    }

    this.analyticsClient.link(id, this.paramsWithAuthMetadata(params));
  }

  modal(id: string, params?: AnalyticsRecord) {
    if (isAnalyticsDisabled()) {
      return;
    }

    this.analyticsClient.modal(id, this.paramsWithAuthMetadata(params));
  }

  processEvent(event: IAnalyticsEvent) {
    switch (event.type) {
      case 'viewed':
        this.viewed(event.id, event.params);
        break;
      case 'action':
        this.action(event.id, event.params);
        break;
      case 'track':
        this.track(event.id, event.params);
        break;
      case 'modal':
        this.modal(event.id, event.params);
        break;
      case 'link':
        this.link(event.id, event.params);
        break;
      default:
        assertUnreachable(event.type, 'Unknown analytics event received');
    }
  }

  private paramsWithAuthMetadata(params?: AnalyticsRecord) {
    return Object.assign({}, this.observabilityAuthMetadata, params);
  }
}
